import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Download, Share } from 'lucide-react';

export default function PWAInstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show if already installed as standalone
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return;

    // Check if dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    // Stagger: wait for cookie consent, and on the landing page let the
    // referral banner finish its ~13s window before showing this prompt
    let timer;
    const delay = window.location.pathname === '/' ? 13000 : 3000;
    const schedule = () => { timer = setTimeout(() => setShowBanner(true), delay); };
    const onConsent = () => schedule();
    const armShow = () => {
      if (localStorage.getItem('taskeri_cookie_consent')) schedule();
      else window.addEventListener('taskeri:cookie-consent', onConsent, { once: true });
    };

    let promptHandler;
    if (isiOS) {
      armShow();
    } else {
      // Android/Desktop: listen for beforeinstallprompt
      promptHandler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        armShow();
      };
      window.addEventListener('beforeinstallprompt', promptHandler);
    }
    return () => {
      clearTimeout(timer);
      window.removeEventListener('taskeri:cookie-consent', onConsent);
      if (promptHandler) window.removeEventListener('beforeinstallprompt', promptHandler);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt, isIOS]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }, []);

  // Never show on admin pages
  if (location.pathname.startsWith('/ti-admin')) return null;

  if (!showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div
        data-testid="pwa-install-banner"
        className="pwa-install-banner"
      >
        <div className="pwa-install-banner-inner">
          <div className="pwa-install-icon-wrap">
            <img src="/favicon-192.png" alt="taskeri®" className="pwa-install-icon" />
          </div>
          <div className="pwa-install-text">
            <p className="pwa-install-title">Install taskeri<sup style={{fontSize:'0.55em'}}>®</sup></p>
            <p className="pwa-install-subtitle">Quick access from your home screen</p>
          </div>
          <button
            data-testid="pwa-install-button"
            onClick={handleInstall}
            className="pwa-install-btn"
          >
            {isIOS ? <Share size={16} /> : <Download size={16} />}
            <span>Install</span>
          </button>
          <button
            data-testid="pwa-install-dismiss"
            onClick={handleDismiss}
            className="pwa-install-dismiss"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSGuide && (
        <div className="pwa-ios-overlay" onClick={handleDismiss}>
          <div className="pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pwa-ios-close" onClick={handleDismiss} aria-label="Close">
              <X size={20} />
            </button>
            <img src="/favicon-192.png" alt="taskeri®" className="pwa-ios-icon" />
            <h3 className="pwa-ios-title">Install taskeri<sup style={{fontSize:'0.55em'}}>®</sup></h3>
            <div className="pwa-ios-steps">
              <div className="pwa-ios-step">
                <span className="pwa-ios-step-num">1</span>
                <p>Tap the <Share size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> <strong>Share</strong> button in Safari</p>
              </div>
              <div className="pwa-ios-step">
                <span className="pwa-ios-step-num">2</span>
                <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
              </div>
              <div className="pwa-ios-step">
                <span className="pwa-ios-step-num">3</span>
                <p>Tap <strong>"Add"</strong> to confirm</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
